import { createSlice } from "@reduxjs/toolkit"
import {
  getAllPlans,
  getPlanDetails,
  getCurrentSubscription,
  getPlanHistory,
  createSubscription,
  upgradeSubscription,
  downgradeSubscription,
  cancelSubscription,
  revokeScheduledCancellation,
  renewSubscription,
  getAllInvoices,
  getInvoiceDetails,
  initiateCheckout,
  verifyPayment,
} from "store/actions/subscriptionAction"

const initialState = {
  // Plans
  plansLoading: false,
  actionLoading: false,
  plans: [],
  planDetails: {},

  // Current subscription
  subscriptionLoading: false,
  currentSubscription: null,
  planHistoryLoading: false,
  planHistory: [],

  // Invoices
  invoicesLoading: false,
  invoices: [],
  invoiceDetails: {},
  totalCount: 0,
  totalPages: null,
  currentPage: 1,
  itemsPerPage: 10,

  // Checkout
  checkoutLoading: false,
  checkoutData: null,
  paymentVerified: false,

  error: null,
}

const getPayloadData = payload => {
  const primary = payload?.data
  if (Array.isArray(primary)) return primary
  if (Array.isArray(primary?.data)) return primary.data
  if (Array.isArray(primary?.items)) return primary.items
  return []
}

const getPayloadObject = payload => {
  const primary = payload?.data
  if (primary && !Array.isArray(primary)) {
    if (primary.data && !Array.isArray(primary.data)) return primary.data
    return primary
  }
  return null
}

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearCheckout(state) {
      state.checkoutData = null
      state.paymentVerified = false
      state.error = null
    },
    clearInvoiceDetails(state) {
      state.invoiceDetails = {}
    },
  },
  extraReducers: builder => {
    // ── GET ALL PLANS ──────────────────────────────────────────────────────────
    builder
      .addCase(getAllPlans.pending, state => {
        state.plansLoading = true
        state.error = null
      })
      .addCase(getAllPlans.fulfilled, (state, action) => {
        state.plansLoading = false
        const { status } = action.payload || {}
        if (status) {
          state.plans = getPayloadData(action.payload)
        }
      })
      .addCase(getAllPlans.rejected, (state, action) => {
        state.plansLoading = false
        state.error = action.error?.message || "Failed to fetch plans"
      })

    // ── GET PLAN DETAILS ───────────────────────────────────────────────────────
    builder
      .addCase(getPlanDetails.pending, state => {
        state.plansLoading = true
        state.planDetails = {}
      })
      .addCase(getPlanDetails.fulfilled, (state, action) => {
        state.plansLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.planDetails = data || {}
        }
      })
      .addCase(getPlanDetails.rejected, state => {
        state.plansLoading = false
      })

    // ── GET CURRENT SUBSCRIPTION ───────────────────────────────────────────────
    builder
      .addCase(getCurrentSubscription.pending, state => {
        state.subscriptionLoading = true
      })
      .addCase(getCurrentSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false
        const { status } = action.payload || {}
        if (status) {
          state.currentSubscription = getPayloadObject(action.payload)
        }
      })
      .addCase(getCurrentSubscription.rejected, state => {
        state.subscriptionLoading = false
      })

    builder
      .addCase(getPlanHistory.pending, state => {
        state.planHistoryLoading = true
      })
      .addCase(getPlanHistory.fulfilled, (state, action) => {
        state.planHistoryLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.planHistory = Array.isArray(data) ? data : []
        }
      })
      .addCase(getPlanHistory.rejected, state => {
        state.planHistoryLoading = false
      })

    // ── CREATE SUBSCRIPTION ────────────────────────────────────────────────────
    builder
      .addCase(createSubscription.pending, state => {
        state.actionLoading = true
      })
      .addCase(createSubscription.fulfilled, state => {
        state.actionLoading = false
      })
      .addCase(createSubscription.rejected, state => {
        state.actionLoading = false
      })

    // ── UPGRADE SUBSCRIPTION ───────────────────────────────────────────────────
    builder
      .addCase(upgradeSubscription.pending, state => {
        state.actionLoading = true
      })
      .addCase(upgradeSubscription.fulfilled, state => {
        state.actionLoading = false
      })
      .addCase(upgradeSubscription.rejected, state => {
        state.actionLoading = false
      })

    // ── DOWNGRADE SUBSCRIPTION ─────────────────────────────────────────────────
    builder
      .addCase(downgradeSubscription.pending, state => {
        state.actionLoading = true
      })
      .addCase(downgradeSubscription.fulfilled, state => {
        state.actionLoading = false
      })
      .addCase(downgradeSubscription.rejected, state => {
        state.actionLoading = false
      })

    // ── CANCEL SUBSCRIPTION ────────────────────────────────────────────────────
    builder
      .addCase(cancelSubscription.pending, state => {
        state.actionLoading = true
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.actionLoading = false
        const { status, data } = action.payload || {}
        if (status && data) {
          state.currentSubscription = data
        }
      })
      .addCase(cancelSubscription.rejected, state => {
        state.actionLoading = false
      })

    builder
      .addCase(revokeScheduledCancellation.pending, state => {
        state.actionLoading = true
      })
      .addCase(revokeScheduledCancellation.fulfilled, (state, action) => {
        state.actionLoading = false
        const { status, data } = action.payload || {}
        if (status && data) {
          state.currentSubscription = data
        }
      })
      .addCase(revokeScheduledCancellation.rejected, state => {
        state.actionLoading = false
      })

    // ── RENEW SUBSCRIPTION ─────────────────────────────────────────────────────
    builder
      .addCase(renewSubscription.pending, state => {
        state.actionLoading = true
      })
      .addCase(renewSubscription.fulfilled, state => {
        state.actionLoading = false
      })
      .addCase(renewSubscription.rejected, state => {
        state.actionLoading = false
      })

    // ── GET ALL INVOICES ───────────────────────────────────────────────────────
    builder
      .addCase(getAllInvoices.pending, state => {
        state.invoicesLoading = true
      })
      .addCase(getAllInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false
        const { status, data, totalCount, totalPages, currentPage } =
          action.payload || {}
        if (status) {
          state.invoices = Array.isArray(data) ? data : []
          state.totalCount = totalCount || 0
          state.totalPages = totalPages || null
          state.currentPage = currentPage || 1
        }
      })
      .addCase(getAllInvoices.rejected, state => {
        state.invoicesLoading = false
      })

    // ── GET INVOICE DETAILS ────────────────────────────────────────────────────
    builder
      .addCase(getInvoiceDetails.pending, state => {
        state.invoicesLoading = true
        state.invoiceDetails = {}
      })
      .addCase(getInvoiceDetails.fulfilled, (state, action) => {
        state.invoicesLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.invoiceDetails = data || {}
        }
      })
      .addCase(getInvoiceDetails.rejected, state => {
        state.invoicesLoading = false
      })

    // ── INITIATE CHECKOUT ──────────────────────────────────────────────────────
    builder
      .addCase(initiateCheckout.pending, state => {
        state.checkoutLoading = true
        state.checkoutData = null
      })
      .addCase(initiateCheckout.fulfilled, (state, action) => {
        state.checkoutLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.checkoutData = data || null
        }
      })
      .addCase(initiateCheckout.rejected, state => {
        state.checkoutLoading = false
      })

    // ── VERIFY PAYMENT ─────────────────────────────────────────────────────────
    builder
      .addCase(verifyPayment.pending, state => {
        state.checkoutLoading = true
        state.paymentVerified = false
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.checkoutLoading = false
        const { status } = action.payload || {}
        state.paymentVerified = !!status
      })
      .addCase(verifyPayment.rejected, state => {
        state.checkoutLoading = false
        state.paymentVerified = false
      })
  },
})

export const { clearCheckout, clearInvoiceDetails } = subscriptionSlice.actions

export default subscriptionSlice.reducer
